<template>
  <v-container fluid grid-list-lg>
    <Create
      :createDialog="createDialog"
      :selectedYear="selectedYear"
      @close="closeCreate"
      @submit="getList"
    />
    <Edit
      :editDialog="editDialog"
      :editTerm="selectedTerm"
      @close="editDialog = false"
      @submit="getList"
    />

    <v-card :loading="loading" outlined>
      <ListTitle
        :showBackButton="true"
        :access="checkRightStatus(37)"
        title="Term"
        @add="add"
      />

      <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="5">
            <v-text-field
              v-model="search"
              outlined
              prepend-inner-icon="search"
              rounded
              dense
              label="Search year or term"
              single-line
              hide-details
              clearable
            />
          </v-col>

          <v-col cols="12" md="7" class="text-md-right">
            <v-chip class="mr-2" outlined>
              Total Years: {{ groupedYears.length }}
            </v-chip>
            <v-chip outlined> Total Terms: {{ filteredTerms.length }} </v-chip>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-text class="pt-4">
        <v-expansion-panels
          v-if="groupedYears.length"
          v-model="openedPanels"
          multiple
          accordion
          focusable
        >
          <v-expansion-panel
            v-for="yearGroup in groupedYears"
            :key="yearGroup.id"
          >
            <v-expansion-panel-header>
              <div
                class="d-flex align-center justify-space-between flex-grow-1 pr-3"
              >
                <div>
                  <div class="text-subtitle-1 font-weight-medium">
                    {{ yearGroup.name }}
                  </div>
                  <div
                    v-if="yearGroup.className"
                    class="text-caption grey--text"
                  >
                    {{ yearGroup.className }}
                  </div>
                </div>

                <div class="d-flex align-center">
                  <v-chip small class="mr-3" color="primary" text-color="white">
                    {{ yearGroup.terms.length }}
                    {{ yearGroup.terms.length === 1 ? "Term" : "Terms" }}
                  </v-chip>

                  <v-btn
                    v-if="checkRightStatus(37)"
                    small
                    color="primary"
                    @click.stop="addTermToYear(yearGroup)"
                  >
                    <v-icon left small>add</v-icon>
                    Add Term
                  </v-btn>
                </div>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <div v-if="yearGroup.terms.length">
                <v-simple-table class="term-table mt-5">
                  <thead>
                    <tr>
                      <th class="text-left">Term</th>
                      <th class="text-left">Start Date</th>
                      <th class="text-left">End Date</th>
                      <th class="text-left">Created At</th>
                      <th class="text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="term in yearGroup.terms" :key="term.id">
                      <td>{{ term.name }}</td>
                      <td>
                        {{
                          term.startDate
                            ? $moment(term.startDate).format("LL")
                            : "N/A"
                        }}
                      </td>
                      <td>
                        {{
                          term.endDate
                            ? $moment(term.endDate).format("LL")
                            : "N/A"
                        }}
                      </td>
                      <td>{{ term.createdAt | moment("LL") }}</td>
                      <td class="text-right">
                        <v-menu bottom left offset-y>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn v-bind="attrs" v-on="on" icon>
                              <v-icon>more_vert</v-icon>
                            </v-btn>
                          </template>

                          <v-list class="menu-button">
                            <v-list-item
                              v-if="checkRightStatus(39)"
                              @click="editTerm(term)"
                            >
                              <v-list-item-title>Edit</v-list-item-title>
                            </v-list-item>

                            <v-list-item
                              v-if="checkRightStatus(40)"
                              @click="deleteTerm(term)"
                            >
                              <v-list-item-title>Delete</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>

              <div
                v-else
                class="py-4 d-flex align-center justify-space-between flex-wrap"
              >
                <div class="grey--text text-body-2">
                  No terms found under this year.
                </div>

                <v-btn
                  v-if="checkRightStatus(37)"
                  small
                  color="primary"
                  @click="addTermToYear(yearGroup)"
                >
                  <v-icon left small>add</v-icon>
                  Add First Term
                </v-btn>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-alert v-else :value="true" color="warning" icon="warning" outlined>
          No matching years or terms found.
        </v-alert>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import termService from "../service";
import { mapGetters } from "vuex";

const Create = () => import("./Create");
const Edit = () => import("./Edit");

export default {
  name: "term-list",
  components: {
    Create,
    Edit,
  },
  data() {
    return {
      createDialog: false,
      editDialog: false,
      selectedTerm: null,
      selectedYear: null,
      terms: [],
      search: null,
      loading: true,
      openedPanels: [],
    };
  },
  created() {
    this.getList();
    document.title = "taleem - Term List";
  },
  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),

    filteredTerms() {
      if (!this.search) return this.terms;

      const search = this.search.toLowerCase();

      return this.terms.filter((term) => {
        const termName = term.name ? term.name.toLowerCase() : "";
        const yearName =
          term.Year && term.Year.name ? term.Year.name.toLowerCase() : "";
        const className =
          term.Year && term.Year.Class && term.Year.Class.name
            ? term.Year.Class.name.toLowerCase()
            : "";

        return (
          termName.includes(search) ||
          yearName.includes(search) ||
          className.includes(search)
        );
      });
    },

    groupedYears() {
      const groupedMap = {};

      this.filteredTerms.forEach((term) => {
        const yearId = term.Year && term.Year.id ? term.Year.id : "no-year";
        const yearName =
          term.Year && term.Year.name ? term.Year.name : "No Year Assigned";
        const className =
          term.Year && term.Year.Class && term.Year.Class.name
            ? term.Year.Class.name
            : "";

        if (!groupedMap[yearId]) {
          groupedMap[yearId] = {
            id: yearId,
            name: yearName,
            className,
            year: term.Year || null,
            terms: [],
          };
        }

        groupedMap[yearId].terms.push(term);
      });

      return Object.values(groupedMap)
        .map((group) => {
          group.terms = group.terms.sort((a, b) => {
            const nameA = a.name || "";
            const nameB = b.name || "";
            return nameA.localeCompare(nameB);
          });
          return group;
        })
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  watch: {
    search() {
      this.expandAllWhenSearching();
    },
  },
  methods: {
    add() {
      this.selectedYear = null;
      this.createDialog = true;
    },

    addTermToYear(yearGroup) {
      this.selectedYear = yearGroup.year;
      this.createDialog = true;
    },

    closeCreate() {
      this.createDialog = false;
      this.selectedYear = null;
    },

    expandAllWhenSearching() {
      if (this.search && this.groupedYears.length) {
        this.openedPanels = this.groupedYears.map((_, index) => index);
      } else {
        this.openedPanels = [];
      }
    },

    async getList() {
      this.loading = true;
      this.editDialog = false;
      this.createDialog = false;
      this.selectedYear = null;

      try {
        const response = await termService.getAll();
        console.log("response", response);
        this.terms = response.data || [];
        this.expandAllWhenSearching();
        return response;
      } catch (error) {
        console.log("error getting terms", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    editTerm(term) {
      this.selectedTerm = term;
      this.editDialog = true;
    },

    deleteTerm(term) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          termService.delete(term.id).then((response) => {
            if (response.data) {
              this.$swal("Deleted!", "Term has been deleted.", "success");
              this.getList();
            }
          });
        }
      });
    },
  },
};
</script>

<style scoped>
.term-table {
  border: 1px solid #eeeeee;
  border-radius: 8px;
  overflow: hidden;
}

.pointer {
  cursor: pointer;
}
</style>